/* eslint-disable spaced-comment */
/* eslint-disable no-useless-catch */
/* eslint-disable no-unused-vars */
import React from 'react';
import { split, ApolloClient, createHttpLink, Observable, ApolloLink } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { PublicClientApplication } from '@azure/msal-browser';
 
import {
  intlLink,
  uniqueRequestIdLink,
  cacheFactory
, isPublicRoute } from '@exo/frontend-common-app-shell';
import baseLoadable from '@loadable/component';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import {
  Spinner
} from '@nextgen/frontend-components-atoms';
import {
  refreshTokenViaMSAL,
  handleApolloError,
  MSAL_CACHE,
  waitForAuthInstance,
  syncTokenWithBackendDirect,
  isInternalUserAuthContext,
  logFeErrorToServer,
  clearMsalStorageAndRedirect
} from './applications-utils';
import cartBaseStoreManager from './services/CartBaseStoreManager';
import cartBaseStoreAPIInterceptor from './services/CartBaseStoreAPIInterceptor';
import baseStoreSyncManager, { getBaseStoreSyncPromise } from './services/BaseStoreSyncManager';
import {
  msalConfig,
  scopes as msalDefaultScopes
} from '../../common/app-shell/client/msal/msalConfig';

const styles = {
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems:'center',
    height:'100vh'
  }
}
const loadable = (fn, opts = {}) => baseLoadable(fn, {
    fallback: <div style={styles.spinner}><Spinner /></div>,
    resolveComponent: loaded => loaded.default,
    ...opts
  });
 
const featureFlag = s => s?.toUpperCase() === 'TRUE';
 
const baseLink =
  process.env.GRAPHQL_BATCH === 'true'
    ? new BatchHttpLink({ uri: process.env.GRAPHQL_ENDPOINT, credentials: 'include' })
    : createUploadLink({
      uri: process.env.GRAPHQL_ENDPOINT,
      credentials: 'include'
    });
 
const contentfulSpaceId = process.env.CONTENTFUL_SPACE_ID;
const contentfulEnvironmentAlias = process.env.CONTENTFUL_ENVIRONMENT_ALIAS;
const contentfulAccessToken = process.env.CONTENTFUL_ACCESS_TOKEN;
const contentfulBaseUrl = process.env.CONTENTFUL_BASE_URL;
const AUTH_REDIRECT_SYNC_TS = 'auth.redirect.sync.ts';
const AUTH_SESSION_HEADERS_READY_TS = 'auth.session.headers.ready.ts';
const MSAL_INTERACTION_LOCK = 'msal.interaction.lock';
 
const contentfulLink = createHttpLink({
  uri: `${contentfulBaseUrl}/spaces/${contentfulSpaceId}/environments/${contentfulEnvironmentAlias}`,
  headers: {
    authorization: `Bearer ${contentfulAccessToken}`
  }
});
 
const httpLink = split(
  operation => operation.getContext().isContentRequest,
  contentfulLink,
  uniqueRequestIdLink.concat(intlLink.concat(baseLink))
);

const setLastRefreshedToken = () => {
  // Updated lastRefreshedToken
};

const getCachedMsalAccessToken = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const cacheRaw = window.sessionStorage.getItem(MSAL_CACHE)
      || window.localStorage.getItem(MSAL_CACHE);
    if (!cacheRaw) {
      return null;
    }

    const parsed = JSON.parse(cacheRaw);
    const accessToken = parsed?.value?.accessToken || parsed?.accessToken;
    const expiresOn = parsed?.expiresOn || parsed?.value?.expiresOn;

    if (!accessToken) {
      return null;
    }

    const expiresOnMs = typeof expiresOn === 'number' ? expiresOn : Number(expiresOn || 0);
    if (expiresOnMs > 0 && expiresOnMs <= (Date.now() + 5000)) {
      return null;
    }

    return accessToken;
  } catch (e) {
    return null;
  }
};

const markAuthRedirectSyncSuccess = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(AUTH_REDIRECT_SYNC_TS, String(Date.now()));
    // Also set the key that authRouteStabilizationLink checks, and dispatch the
    // event it listens for, so /auth/asmUser queries don't have to wait 2500ms.
    window.sessionStorage.setItem(AUTH_SESSION_HEADERS_READY_TS, String(Date.now()));
    window.dispatchEvent(new Event('authSessionHeadersReady'));
  } catch (e) {
    // no-op
  }
};

const clearMsalInteractionLock = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(MSAL_INTERACTION_LOCK);
  } catch (e) {
    // no-op
  }
};

if (typeof window !== 'undefined') {
  // One-time recovery for stale chunk/CDN cache mismatch errors
  const CHUNK_RELOAD = 'chunk-load-recovery-attempted';
  const hasAttemptedChunkRecovery = window.sessionStorage.getItem(CHUNK_RELOAD) === 'true';

  window.addEventListener('error', event => {
    const message = event?.message || event?.error?.message || '';
    const filename = event?.filename || '';
    const isChunkLoadError =
      String(message).includes('ChunkLoadError')
      || String(message).includes('Loading chunk')
      || /\/(\d+)\.chunk\.js/i.test(String(filename));

    if (!isChunkLoadError || hasAttemptedChunkRecovery) {
      return;
    }

    try {
      window.sessionStorage.setItem(CHUNK_RELOAD, 'true');
      const current = new URL(window.location.href);
      current.searchParams.set('_chunkRetry', String(Date.now()));
      window.location.replace(current.toString());
    } catch (reloadError) {
      // Failed to perform chunk-recovery reload
    }
  });
}

// HttpOnly auth cookie is owned by backend authCallback; frontend must not write/read token cookies.
const authRouteStabilizationLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext?.() || {};

  if (typeof window === 'undefined' || context.isContentRequest || !isInternalUserAuthContext()) {
    return forward(operation);
  }

  const currentPath = window.location?.pathname || '';
  if (!currentPath.includes('/auth/asmUser')) {
    return forward(operation);
  }

  const operationName = operation?.operationName || '';
  const shouldStabilize = operationName === 'AsmCustomerList' || operationName === 'Roles';
  if (!shouldStabilize) {
    return forward(operation);
  }

  let hasReadyMarker = false;
  try {
    const readyTs = Number(window.sessionStorage.getItem(AUTH_SESSION_HEADERS_READY_TS) || 0);
    hasReadyMarker = readyTs > 0 && (Date.now() - readyTs) < 30000;
  } catch (e) {
    hasReadyMarker = false;
  }

  if (hasReadyMarker) {
    return forward(operation);
  }

  return new Observable(observer => {
    let settled = false;
    let onReady;
    const settle = () => {
      if (settled) return;
      settled = true;
      if (onReady) {
        window.removeEventListener('authSessionHeadersReady', onReady);
      }
      forward(operation).subscribe({
        next: value => observer.next(value),
        error: err => observer.error(err),
        complete: () => observer.complete()
      });
    };

    onReady = () => settle();
    window.addEventListener('authSessionHeadersReady', onReady, { once: true });
    setTimeout(settle, 6000);

    return () => {
      window.removeEventListener('authSessionHeadersReady', onReady);
    };
  });
});

const tokenInjectionLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext?.() || {};
  const currentPath = typeof window !== 'undefined' ? (window.location?.pathname || '') : '';
  const isAuthRoute = currentPath.includes('/auth/');

  if (isPublicRoute()) {
    return forward(operation);
  }

  if (context.isContentRequest || (!isInternalUserAuthContext() && !isAuthRoute)) {
    return forward(operation);
  }

  const tokenFromCache = getCachedMsalAccessToken();
  if (!tokenFromCache) {
    return forward(operation);
  }

  const existingHeaders = context.headers || {};
  if (!existingHeaders.Authorization && !existingHeaders.authorization) {
    operation.setContext({
      ...context,
      headers: {
        ...existingHeaders,
        Authorization: `Bearer ${tokenFromCache}`
      }
    });
  }

  return forward(operation);
});
 
const wsLink =
  process.env.GRAPHQL_WEBSOCKET &&
  new WebSocketLink({
    uri: process.env.GRAPHQL_WEBSOCKET,
    options: {
      reconnect: true
    }
  });
 
const link = wsLink
  ? split(
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query);
      return kind === 'OperationDefinition' && operation === 'subscription';
    },
    wsLink,
    httpLink
  )
  : httpLink;

const cartBaseStoreLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext?.() || {};
  const variables = operation.variables || {};
  const cartId = variables.cartId || context.cartId;
  
  // Read current baseStore from session context (real-time check for multi-tab scenarios)
  let currentSessionBaseStore = context.baseStore;
  if (!currentSessionBaseStore && typeof window !== 'undefined') {
    try {
      const sessionData = window.localStorage.getItem('sessionContext');
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        currentSessionBaseStore = parsed.baseStore;
      }
    } catch (e) {
      // Continue without session baseStore
    }
  }

  // Return a function that either allows the request or waits for sync to complete
  return new Observable(observer => {
    const executeRequest = () => {
      if (cartId) {
        const mappedStore = cartBaseStoreManager.getCartBaseStore(cartId);
        const requestBaseStore = variables.baseStore || context.baseStore;

        // CRITICAL: If request baseStore doesn't match current session baseStore, block the request
        if (currentSessionBaseStore && requestBaseStore && currentSessionBaseStore !== requestBaseStore) {
          const mismatchError = new Error(
            `Session baseStore changed. Expected ${currentSessionBaseStore}, got ${requestBaseStore}`
          );
          mismatchError.code = 'SESSION_BASESTORE_MISMATCH';
          mismatchError.currentBaseStore = currentSessionBaseStore;
          mismatchError.requestBaseStore = requestBaseStore;
          
          // Emit event to trigger Cart refresh with new baseStore
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('cartRefreshNeeded', {
              detail: { cartId, newBaseStore: currentSessionBaseStore }
            }));
          }
          
          return observer.error(mismatchError);
        }

        // Auto-inject mapped baseStore if missing
        if (!requestBaseStore && mappedStore) {
          operation.setContext({
            ...context,
            cartId,
            baseStore: mappedStore
          });
        } else if (!requestBaseStore && currentSessionBaseStore) {
          // Use current session baseStore
          operation.setContext({
            ...context,
            cartId,
            baseStore: currentSessionBaseStore
          });
        }
      }

      const validationResult = cartBaseStoreAPIInterceptor.validateRequest(operation);
      if (!validationResult.isValid) {
        return observer.error(validationResult.error);
      }

      const subscription = forward(operation).subscribe({
        next: result => observer.next(result),
        error: error => {
          if (cartId) {
            const handled = cartBaseStoreAPIInterceptor.handleAPIError(error, cartId);
            if (handled?.shouldRefresh && typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('cartRefreshNeeded', {
                detail: { cartId, newBaseStore: handled.newBaseStore }
              }));
            }
          }
          observer.error(error);
        },
        complete: () => observer.complete()
      });

      return () => subscription.unsubscribe();
    };

    // If there's a pending baseStore sync, wait for it to complete before sending request
    const pendingSync = getBaseStoreSyncPromise();
    if (pendingSync) {
      pendingSync.then(
        () => {
          executeRequest();
        },
        (error) => {
          // Continue with request anyway - let backend handle it
          executeRequest();
        }
      );
    } else {
      executeRequest();
    }
  });
});

// MSAL instance and scopes for token refresh (reuse shared msalConfig)
const envScopes = (process.env.AAD_SCOPES || '')
  .split(/[ ,]+/)
  .map(s => s.trim())
  .filter(Boolean);
const effectiveScopes = envScopes.length ? envScopes : msalDefaultScopes;

let msalInstance = null;
if (msalConfig?.auth?.clientId && msalConfig?.auth?.authority && msalConfig?.auth?.redirectUri) {
  try {
    msalInstance = new PublicClientApplication(msalConfig);
  } catch (e) {
    logFeErrorToServer({ message: `MSAL PublicClientApplication constructor failed: ${e?.message || 'unknown'}`, stackTrace: e?.stack, component: 'applications.js', type: 'error', statusCode: 500 });
  }
}

const msalConfigured = !!(msalInstance && effectiveScopes.length);
let hasLoggedMsalConfigWarning = false;

// Track completion of async MSAL redirect/account initialization to avoid auth races
let msalStateInitializationPromise = Promise.resolve();

const waitForMsalStateInitialization = async (timeoutMs = 4000) => {
  try {
    await Promise.race([
      msalStateInitializationPromise,
      new Promise(resolve => {
        setTimeout(resolve, timeoutMs);
      })
    ]);
  } catch (error) {
    // MSAL state initialization failed/timed out, continuing request flow
  }
};

const refreshAccessToken = () => {
  if (!msalConfigured) {
    if (!hasLoggedMsalConfigWarning) {
      hasLoggedMsalConfigWarning = true;
    }
    return null;
  }
  return refreshTokenViaMSAL(msalInstance, effectiveScopes);
};

/**
 * Check if token needs refresh (has expired or expires within 5 minutes)
 * @returns {Promise<boolean>} true if token refresh is needed
 */
const isTokenRefreshNeeded = async () => {
  if (typeof window === 'undefined') return false;
  
  try {
    const cacheStr = window.sessionStorage.getItem(MSAL_CACHE) || 
                     window.localStorage.getItem(MSAL_CACHE);
    
    if (!cacheStr) {
      return true;
    }
    
    const cache = JSON.parse(cacheStr);
    const expiresOn = cache?.expiresOn || cache?.value?.expiresOn;
    
    if (!expiresOn) {
      return true;
    }
    
    const now = Date.now();
    
    // 🔧 FIX: Use msalTokenRefreshTime from cache (3-min buffer set by refreshTokenViaMSAL)
    // instead of a hardcoded 5-minute window from raw expiresOn. Using the raw 5-minute window
    // triggered forceRefresh:true on every request for the last 5 minutes, causing premature
    // SSO redirects when those forced network calls failed.
    const msalTokenRefreshTime = cache?.value?.msalTokenRefreshTime || cache?.msalTokenRefreshTime;
    
    let needsRefresh;
    if (msalTokenRefreshTime) {
      needsRefresh = now >= msalTokenRefreshTime;
    } else {
      // No refresh-time hint — fall back to 3-minute buffer before raw expiry
      const threeMinutesMs = 3 * 60 * 1000;
      const timeUntilExpiry = expiresOn - now;
      needsRefresh = timeUntilExpiry < threeMinutesMs;
    }
    
    return needsRefresh;
  } catch (error) {
    return true; // Assume refresh needed on error
  }
};

// Track token refresh failures to prevent infinite retry loops
const refreshFailureTracker = {
  failureCount: 0,
  lastFailureTime: null,
  maxRetries: 3,
  resetThreshold: 5 * 60 * 1000 // Reset after 5 minutes of no failures
};

// Global state for session timeout modal
const sessionTimeoutModalState = {
  isOpen: false,
  onRefresh: null,
  onSignIn: null,
  updateState: null
};

// Register MSAL instance and scopes globally for SessionTimeoutManager access
const initializeGlobalMSALState = async () => {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-underscore-dangle
    if (!window.__MSAL_REDIRECT_SYNC_PROMISE__) {
      // eslint-disable-next-line no-underscore-dangle
      window.__MSAL_REDIRECT_SYNC_PROMISE__ = Promise.resolve();
    }
    // eslint-disable-next-line no-underscore-dangle
    if (typeof window.__MSAL_REDIRECT_SYNC_IN_PROGRESS__ !== 'boolean') {
      // eslint-disable-next-line no-underscore-dangle
      window.__MSAL_REDIRECT_SYNC_IN_PROGRESS__ = false;
    }

    if (msalInstance?.initialize && msalInstance.initialized === false) {
      try {
        await msalInstance.initialize();
      } catch (initError) {
        logFeErrorToServer({ message: `MSAL initialize failed: ${initError?.message || 'unknown'}`, stackTrace: initError?.stack, component: 'applications.js - initializeGlobalMSALState', type: 'error', statusCode: 500 });
      }
    }

    const msalConfigInfo = { instance: msalInstance, scopes: effectiveScopes };
    window.msalConfig = msalConfigInfo;
    if (!window.sessionTimeoutState) {
      window.sessionTimeoutState = sessionTimeoutModalState;
    }
    
    // Expose refresh failure tracker for debugging and monitoring
    // eslint-disable-next-line no-underscore-dangle
    window.__REFRESH_FAILURE_TRACKER__ = refreshFailureTracker;
    
    // Expose refresh failure tracker for debugging and monitoring
    // eslint-disable-next-line no-underscore-dangle
    window.__REFRESH_FAILURE_TRACKER__ = refreshFailureTracker;
    
    // Expose BaseStoreSyncManager globally for Cart1.js access
    // eslint-disable-next-line no-underscore-dangle
    window.__BASESTORE_SYNC_MANAGER__ = baseStoreSyncManager;
    
    // Handle MSAL redirect result from loginRedirect (for session timeout re-auth)
    if (msalInstance && msalInstance.handleRedirectPromise) {
      try {
        const hash = window.location.hash || '';
        const search = window.location.search || '';
        const hasRedirectResponse =
          hash.includes('code=')
          || hash.includes('id_token=')
          || hash.includes('access_token=')
          || (hash.includes('state=') && hash.includes('session_state='))
          || search.includes('code=')
          || search.includes('id_token=')
          || search.includes('access_token=');

        // eslint-disable-next-line no-underscore-dangle
        window.__MSAL_REDIRECT_HANDLED__ = false;
        // eslint-disable-next-line no-underscore-dangle
        window.__MSAL_REDIRECT_SYNC_IN_PROGRESS__ = true;

        const redirectHandlingPromise = hasRedirectResponse
          ? msalInstance.handleRedirectPromise()
          : Promise.resolve(null);

        // eslint-disable-next-line no-underscore-dangle
        window.__MSAL_REDIRECT_SYNC_PROMISE__ = redirectHandlingPromise;

        const redirectResult = await redirectHandlingPromise;
        // eslint-disable-next-line no-underscore-dangle
        window.__MSAL_LAST_REDIRECT_RESULT__ = redirectResult || null;
        if (redirectResult) {
          clearMsalInteractionLock();
          
          // CRITICAL: Persist account to localStorage so it survives page navigation
          if (redirectResult.account) {
            try {
              const accountInfo = {
                homeAccountId: redirectResult.account.homeAccountId,
                environment: redirectResult.account.environment,
                tenantId: redirectResult.account.tenantId,
                username: redirectResult.account.username,
                localAccountId: redirectResult.account.localAccountId,
                name: redirectResult.account.name,
                idTokenClaims: redirectResult.account.idTokenClaims
              };
              window.localStorage.setItem('msal.account', JSON.stringify(accountInfo));
              try {
                const staleMsalKeys = Object.keys(window.sessionStorage)
                  .concat(Object.keys(window.localStorage))
                  .filter(k => k.includes('refreshtoken') || k.includes('accesstoken'));
                staleMsalKeys.forEach(k => {
                  try { window.sessionStorage.removeItem(k); } catch (e) { /* ignore */ }
                  try { window.localStorage.removeItem(k); } catch (e) { /* ignore */ }
                });
              } catch (purgeError) {
                // Non-fatal — continue even if purge fails
              }
              msalInstance.setActiveAccount(redirectResult.account);
            } catch (saveError) {
              logFeErrorToServer({ message: `Could not persist MSAL account: ${saveError?.message || 'unknown'}`, stackTrace: saveError?.stack, component: 'applications.js - initializeGlobalMSALState', type: 'warn', statusCode: 500 });
            }
          }
          
          // 🔧 NEW: Cache the token immediately from redirect result so it's available for proactive link
          // This prevents expired token errors on the first page load after redirect
          if (redirectResult.accessToken) {
            try {
              const expiresOnTime = typeof redirectResult.expiresOn === 'number' 
                ? redirectResult.expiresOn 
                : new Date(redirectResult.expiresOn).getTime();
              
              const msalTokenRefreshTime = expiresOnTime - (3 * 60 * 1000);
              
              const cacheEntry = {
                expiresOn: expiresOnTime,
                value: {
                  ...redirectResult,
                  expiresOn: expiresOnTime,
                  msalTokenRefreshTime
                }
              };
              
              window.sessionStorage.setItem(MSAL_CACHE, JSON.stringify(cacheEntry));
              window.localStorage.setItem(MSAL_CACHE, JSON.stringify(cacheEntry));
              
              // Also cache to localStorage.sessionContext for proactive link access
              if (redirectResult.account) {
                const sessionData = {
                  id: redirectResult.account.homeAccountId || redirectResult.account.localAccountId,
                  email: redirectResult.account.username || redirectResult.account.userPrincipalName,
                  username: redirectResult.account.username,
                  firstName: redirectResult.account.name || '',
                  lastName: '',
                  isInternal: true,
                  type: 'USER'
                };
                window.localStorage.setItem('sessionContext', JSON.stringify(sessionData));
                window.sessionStorage.setItem('sessionContext', JSON.stringify(sessionData));
              }
              
              try {
                const redirectSyncPromise = (async () => {
                  const auth = await waitForAuthInstance(5000, 100);
                  if (auth && (auth.getMsalAccessToken || auth.syncMsalSession || auth.gqlAuthCallbackMSAL)) {
                    try {
                      if (auth.getMsalAccessToken) {
                        await auth.getMsalAccessToken();
                        if (redirectResult.accessToken && auth.syncMsalSession) {
                          await auth.syncMsalSession(redirectResult.accessToken, { updateUserInfo: true });
                        }
                      } else if (auth.syncMsalSession) {
                        await auth.syncMsalSession(redirectResult.accessToken, { updateUserInfo: true });
                      } else {
                        await auth.gqlAuthCallbackMSAL(redirectResult.accessToken);
                        if (auth.gqlUserInfo && auth.setUserInfo) {
                          try {
                            await auth.setUserInfo(await auth.gqlUserInfo(true));
                          } catch (userInfoError) {
                            logFeErrorToServer({ message: `Could not refresh user info after backend sync: ${userInfoError?.message || 'unknown'}`, stackTrace: userInfoError?.stack, component: 'applications.js - initializeGlobalMSALState', type: 'warn', statusCode: 500 });
                          }
                        }
                      }
                    } catch (callbackError) {
                      // Backend sync via Auth.js failed
                      throw callbackError;
                    }
                    markAuthRedirectSyncSuccess();
                  } else {
                    // Fallback: sync token directly with backend so HttpOnly session cookie is still established
                    let codeVerifierFromSession = null;
                    try {
                      const sessionData = window.localStorage.getItem('sessionContext');
                      if (sessionData) {
                        const parsed = JSON.parse(sessionData);
                        codeVerifierFromSession = parsed.codeVerifier || null;
                      }
                    } catch (e) {
                      // continue without codeVerifier
                    }

                    try {
                      await syncTokenWithBackendDirect(redirectResult.accessToken, codeVerifierFromSession);
                      markAuthRedirectSyncSuccess();
                    } catch (directSyncError) {
                      logFeErrorToServer({ message: `Direct GraphQL fallback sync failed: ${directSyncError?.message || 'unknown'}`, stackTrace: directSyncError?.stack, component: 'applications.js - initializeGlobalMSALState', type: 'error', statusCode: 500 });
                    }
                  }
                  // eslint-disable-next-line no-underscore-dangle
                  window.__MSAL_REDIRECT_SYNC_IN_PROGRESS__ = false;
                })();

                // eslint-disable-next-line no-underscore-dangle
                window.__MSAL_REDIRECT_SYNC_PROMISE__ = redirectSyncPromise;
                await redirectSyncPromise;
              } catch (syncError) {
                logFeErrorToServer({ message: `MSAL redirect sync failed: ${syncError?.message || 'unknown'}`, stackTrace: syncError?.stack, component: 'applications.js - initializeGlobalMSALState', type: 'error', statusCode: 500 });
                // eslint-disable-next-line no-underscore-dangle
                window.__MSAL_REDIRECT_SYNC_IN_PROGRESS__ = false;
              }
            } catch (cacheError) {
              logFeErrorToServer({ message: `Failed to cache MSAL token from redirect result: ${cacheError?.message || 'unknown'}`, stackTrace: cacheError?.stack, component: 'applications.js - initializeGlobalMSALState', type: 'error', statusCode: 500 });
            }
          }
        }
      } catch (error) {
        if (String(error?.message || '').includes('state_not_found')) {
          clearMsalInteractionLock();
          try {
            window.sessionStorage.removeItem('auth.internal.flow.lock');
          } catch (lockClearError) {
            // no-op
          }
        }
      } finally {
        // eslint-disable-next-line no-underscore-dangle
        window.__MSAL_REDIRECT_SYNC_IN_PROGRESS__ = false;
        // eslint-disable-next-line no-underscore-dangle
        window.__MSAL_REDIRECT_SYNC_PROMISE__ = Promise.resolve();
        // eslint-disable-next-line no-underscore-dangle
        window.__MSAL_REDIRECT_HANDLED__ = true;
      }
    }
    
    // CRITICAL: Restore MSAL account from localStorage if no active account exists
    // This ensures account persists after page reload/navigation
    if (msalInstance && !msalInstance.getActiveAccount()) {
      try {
        const savedAccountJson = window.localStorage.getItem('msal.account');
        if (savedAccountJson) {
          const savedAccount = JSON.parse(savedAccountJson);
          
          // Check if this account still exists in MSAL cache
          const accounts = msalInstance.getAllAccounts();
          const matchingAccount = accounts.find(acc => 
            acc.homeAccountId === savedAccount.homeAccountId ||
            acc.username === savedAccount.username
          );
          
          if (matchingAccount) {
            msalInstance.setActiveAccount(matchingAccount);
          }
        }
      } catch (restoreError) {
        // Could not restore MSAL account from localStorage
      }
    }
  }
};

// Call on module load and expose promise for request gating
msalStateInitializationPromise = initializeGlobalMSALState().catch(e => {
  logFeErrorToServer({ message: `Error initializing MSAL state: ${e?.message || 'unknown'}`, stackTrace: e?.stack, component: 'applications.js', type: 'error', statusCode: 500 });
});

const showSessionRefreshOverlay = () => {
  // Signal to show session timeout modal
  if (sessionTimeoutModalState.updateState) {
    sessionTimeoutModalState.updateState(true);
  }
};

const hideSessionRefreshOverlay = () => {
  // Signal to hide session timeout modal
  if (sessionTimeoutModalState.updateState) {
    sessionTimeoutModalState.updateState(false);
  }
};

// Proactive token refresh link - checks and refreshes token BEFORE each request
let tokenRefreshInProgress = false;

// Reset failure counter if enough time has passed since last failure
const resetRefreshFailureCounter = () => {
  const now = Date.now();
  if (refreshFailureTracker.lastFailureTime && 
      now - refreshFailureTracker.lastFailureTime > refreshFailureTracker.resetThreshold) {
    refreshFailureTracker.failureCount = 0;
  }
};

// Increment failure counter and check if we've exceeded max retries
const recordTokenRefreshFailure = () => {
  resetRefreshFailureCounter();
  refreshFailureTracker.failureCount += 1;
  refreshFailureTracker.lastFailureTime = Date.now();
  
  const retriesRemaining = Math.max(0, refreshFailureTracker.maxRetries - refreshFailureTracker.failureCount);
  
  // If max retries exceeded on auth pages, force redirect to login — but ONLY
  // if the cached token is actually expired. Transient network failures can cause
  // 3 consecutive "failures" even when the token still has plenty of time left;
  // clearing the MSAL cache in that case causes "no account found" on the next load.
  if (refreshFailureTracker.failureCount >= refreshFailureTracker.maxRetries) {

    // Only act if the token is actually expired (transient network failures should not
    // force-redirect when the token still has time left)
    let tokenIsActuallyExpired = true;
    try {
      const cacheStr = window.sessionStorage.getItem(MSAL_CACHE) || window.localStorage.getItem(MSAL_CACHE);
      if (cacheStr) {
        const cache = JSON.parse(cacheStr);
        const expiresOn = cache?.expiresOn || cache?.value?.expiresOn;
        if (expiresOn && expiresOn > Date.now()) {
          tokenIsActuallyExpired = false;
        }
      }
    } catch (checkError) {
      // If we can't read the cache, assume expired
    }

    if (tokenIsActuallyExpired) {
      clearMsalStorageAndRedirect();
    }
  }
};

const proactiveTokenRefreshLink = new ApolloLink((operation, forward) => {
  // Return early if MSAL not configured
  if (!msalConfigured) {
    return forward(operation);
  }

  if (isPublicRoute()) {
    return forward(operation);
  }

  // Apply proactive MSAL refresh only for internal auth context
  if (!isInternalUserAuthContext()) {
    return forward(operation);
  }

  // Skip proactive refresh while user is on auth routes to avoid redirect-phase loops
  if (typeof window !== 'undefined' && window.location?.pathname?.includes('/auth/')) {
    return forward(operation);
  }

  // Forward request and check token status
  return new Observable(observer => {
    let waitAttempts = 0;
    const maxWaitAttempts = 3; // Max times to wait for refresh already in progress

    // Ensure MSAL redirect/account initialization has had a chance to complete
    waitForMsalStateInitialization().then(() => {
      // Check if token needs refresh
      isTokenRefreshNeeded().then(needsRefresh => {
      if (!needsRefresh) {
        // Token is still valid, proceed with request
        forward(operation).subscribe({
          next: result => observer.next(result),
          error: error => observer.error(error),
          complete: () => observer.complete()
        });
        return;
      }
      
      if (tokenRefreshInProgress) {
        waitAttempts += 1;
        if (waitAttempts > maxWaitAttempts) {
          // Exceeded max wait attempts - proceed anyway
          forward(operation).subscribe({
            next: result => observer.next(result),
            error: error => observer.error(error),
            complete: () => observer.complete()
          });
          return;
        }
        // Wait a bit and retry
        setTimeout(() => {
          forward(operation).subscribe({
            next: result => observer.next(result),
            error: error => observer.error(error),
            complete: () => observer.complete()
          });
        }, 500);
        return;
      }

      tokenRefreshInProgress = true;
      refreshAccessToken().then(token => {
        tokenRefreshInProgress = false;
        
        if (token) {
          // Successfully refreshed token - reset failure counter
          refreshFailureTracker.failureCount = 0;
          refreshFailureTracker.lastFailureTime = null;
          
          // CRITICAL: Store the refreshed token globally so tokenInjectionLink uses it immediately
          setLastRefreshedToken(token);
          
          forward(operation).subscribe({
            next: result => observer.next(result),
            error: error => observer.error(error),
            complete: () => observer.complete()
          });
        } else {
          // Token refresh returned null - record as failure
          // Log to server - this typically means invalid_grant or no MSAL account
          try {
            // eslint-disable-next-line no-underscore-dangle
            const authForNullLog = typeof window !== 'undefined' ? window.__AUTH_INSTANCE__ : null;
            if (authForNullLog?.logError?.logErrorToServer) {
              authForNullLog.logError.logErrorToServer({
                message: `Proactive token refresh returned null - token could not be refreshed (possible invalid_grant/expired SPA refresh token)`,
                stackTrace: undefined,
                component: 'applications.js - proactiveTokenRefreshLink',
                type: 'warn',
                statusCode: 401
              });
            }
          } catch (logErr) {
            // fail silently
          }
          recordTokenRefreshFailure();
          // Proceed anyway - error handler will manage it
          forward(operation).subscribe({
            next: result => observer.next(result),
            error: error => observer.error(error),
            complete: () => observer.complete()
          });
        }
      }).catch(error => {
        tokenRefreshInProgress = false;
        // Log unrecoverable token errors to server
        try {
          // eslint-disable-next-line no-underscore-dangle
          const authForLog = typeof window !== 'undefined' ? window.__AUTH_INSTANCE__ : null;
          if (authForLog?.logError?.logErrorToServer) {
            authForLog.logError.logErrorToServer({
              message: `Proactive token refresh failed: ${error?.originalErrorCode || error?.message || 'unknown'}${error?.isUnrecoverable ? ' (UNRECOVERABLE - invalid_grant/expired)' : ''}`,
              stackTrace: error?.stack,
              component: 'applications.js - proactiveTokenRefreshLink',
              type: 'error',
              statusCode: 401
            });
          }
        } catch (logErr) {
          // fail silently
        }
        // Unrecoverable (e.g. SPA refresh token expired, monitor_window_timeout due to
        // X-Frame-Options:deny): silent refresh will never succeed — redirect to SSO
        // immediately to break the infinite failure loop.
        if (error?.isUnrecoverable) {
          clearMsalStorageAndRedirect();
          return;
        }
        recordTokenRefreshFailure();
        // Proceed anyway - error handler will manage it
        forward(operation).subscribe({
          next: result => observer.next(result),
          error: err => observer.error(err),
          complete: () => observer.complete()
        });
      });
      }).catch(error => {
        // Proceed anyway
        forward(operation).subscribe({
          next: result => observer.next(result),
          error: err => observer.error(err),
          complete: () => observer.complete()
        });
      });
    });
  });
});

// Error link that delegates auth retry logic to shared handler (only when MSAL is configured)
const authRetryErrorLink = msalConfigured
  ? onError(errorContext => handleApolloError(
    { ...errorContext, Observable },
    refreshAccessToken,
    showSessionRefreshOverlay,
    hideSessionRefreshOverlay
  ))
  : null;

// Chain: proactive token refresh → token injection → error handling → main link
// Order is critical:
// 1. cartBaseStoreLink - manages cart context
// 2. proactiveTokenRefreshLink - checks and refreshes token if needed, caches it
// 3. tokenInjectionLink - reads fresh cached token and injects into cookie
// 4. authRetryErrorLink - handles auth errors with retry logic
// 5. link - main HTTP/WebSocket link
const linkWithAuthRetry = msalConfigured
  ? cartBaseStoreLink
      .concat(proactiveTokenRefreshLink)
      .concat(authRouteStabilizationLink)
      .concat(tokenInjectionLink)
      .concat(authRetryErrorLink)
      .concat(link)
  : cartBaseStoreLink.concat(link);

const normalizeBoolean = (value, fallback = false) => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};
 
const modules = {
  authentication: loadable(() =>
    import('@nextgen/frontend-features-authentication-app/client/App')
  ),
  accountProfile: loadable(() =>
    import('@exo/frontend-features-account-profile/client/App')
  ),
  content: loadable(() =>
    import('@exo/frontend-features-content-app/client/App')
  ),
  catalog: loadable(() =>
    import('@exo/frontend-features-catalog-app/client/App')
  ),
  cart: loadable(() => import('@exo/frontend-features-cart-app/client/App')),
  checkout: loadable(() =>
    import('@exo/frontend-features-checkout-app/client/App')
  )
};
 
export default {
  cms: {},
  modules: {
    '/': {
      url: 'http://localhost:3000',
      modules: {
        '/content': modules.content,
        '/catalog': modules.catalog,
        '/cart': modules.cart,
        '/account-profile': modules.accountProfile,
        '/checkout': modules.checkout,
        '/itr': modules.checkout,
        '/auth': modules.authentication,
        '/cartsummary':modules.cartSummary
      }
    }
  },
  config: {
    appName: process.env.APP_NAME,
    favicon: process.env.FAV_ICON,
    requiresCookieConsent: true,
    redirects: {
      authErrorRedirect: '/auth/error'
    },
    maintenancePageAuth: process.env.MAINTENANCE_PAGE_AUTH,
    mvp2: {
      isEnabled: normalizeBoolean(process.env.MVP2_IS_ENABLED)
    },
    itr: {
      isEnabled: normalizeBoolean(process.env.IS_ITR_ENABLED)
    },
    ofac: {
      isEnabled: normalizeBoolean(process.env.OFAC_IS_ENABLED)
    },
    fuelPoint: {
      isVisible: normalizeBoolean(process.env.FUEL_POINT_IS_VISIBLE)
    },
    languageSelector: {
      isEnabled: normalizeBoolean(process.env.LANGUAGE_SELECTOR_IS_ENABLED)
    },
    spot: { 
      // Support both current and legacy env var names
      isEnabled: normalizeBoolean(
        process.env.IS_SPOT_ENABLED ?? process.env.ENV_IS_SPOT_ENABLED
      )
    },
    amendOrderEnquiry: {
      isEnabled: normalizeBoolean(process.env.IS_AMEND_ORDER_ENQUIRY_ENABLED)
    },
    addNewBillingAddress: {
      isEnabled: normalizeBoolean(process.env.IS_ADD_NEW_BILLING_ADDRESS_ENABLED)
    },
    taxExemption: {
      isEnabled: normalizeBoolean(process.env.IS_TAX_EXEMPTION_ENABLED)
    },
    annualCertificate: {
      isEnabled: normalizeBoolean(process.env.IS_ANNUAL_CERTIFICATE_ENABLED)
    },
    enquiryToOrder: {
      isEnabled: normalizeBoolean(process.env.IS_ENQUIRY_TO_ORDER_ENABLED)
    },
    notifications: {
      isEnabled: normalizeBoolean(process.env.IS_NOTIFICATIONS_ENABLED)
    },
    extraCharge: {
      isEnabled: normalizeBoolean(process.env.IS_EXTRA_CHARGE)
    },
    readOnly: {
      roles: (process.env.READONLY_ROLES || '').split(',').map(r => r.trim()).filter(Boolean)
    },
    anag: {
      portalUrl: process.env.ANAG_PORTAL_URL
    },
    graphql: {
      baseUri: process.env.GRAPHQL_ENDPOINT,
      baseWsUri: process.env.GRAPHQL_WEBSOCKET,
      batch: process.env.GRAPHQL_BATCH === 'true'
    },
    plp: {
      aralResourcesBaseUrl: process.env.ARAL_RESOURCES_BASE_URL,
      resourcesBaseUrl: process.env.RESOURCES_BASE_URL,
      resourcesPDS: process.env.RESOURCES_PDS,
      resourcesMSDS: process.env.RESOURCES_MSDS,
      packConfigAttrId: process.env.PACK_CONFIGURATION_ATTRIBUTE_ID,
      packagingtypeAttrId: process.env.PACK_TYPE_ATTRIBUTE_ID,
      oemDataAttrId: process.env.OEM_DATA_ATTRIBUTE_ID,
      totalPackAttrId: process.env.TOTAL_PACKS_ATTRIBUTE_ID,
      packSizeAttrId: process.env.PACK_SIZE_ATTRIBUTE_ID,
      fillUnitAttrId: process.env.FILL_UNIT_ATTRIBUTE_ID,
      netVolumeAttrId: process.env.NET_VOLUME_ATTRIBUTE_ID,
      netWeightAttrId: process.env.NET_WEIGHT_ATTRIBUTE_ID,
      madeToOrderId: process.env.MADE_TO_ORDER_ID,
      weightUnits: process.env.WEIGHT_UNITS,
      volumeUnits: process.env.VOLUME_UNITS,
      includeFacets: process.env.INCLUDE_FACETS
    },
    contentful: {
      translationKey: process.env.CONTENTFUL_TRANSLATION_KEY || 'main',
      hasContentManagement: process.env.HAS_CONTENT_MANAGEMENT === 'true',
      contentfulSpaceId: process.env.CONTENTFUL_SPACE_ID,
      contentfulEnvironmentAlias: process.env.CONTENTFUL_ENVIRONMENT_ALIAS,
      contentfulAccessToken: process.env.CONTENTFUL_ACCESS_TOKEN,
      contentfulBaseUrl: process.env.CONTENTFUL_BASE_URL
    },
    cip: {
      baseUri: process.env.CIP_URI,
      clientId: process.env.CIP_CLIENT_ID,
      redirectUri: process.env.CIP_REDIRECT_URI,
      failureUri: process.env.CIP_FAILURE_URI,
      baseMuleUri: process.env.CIP_MULE_URI,
      muleCodeChallengeMethod: process.env.CIP_MULE_CODE_CHALLENGE_METHOD,
      muleNonce: process.env.CIP_MULE_NONCE,
      muleState: process.env.CIP_MULE_STATE,
      registrationScope: process.env.CIP_REGISTRATION_SCOPE,
      passwordUrl: process.env.CIP_PASSWORD_LINK,
      registrationResponseType: process.env.CIP_REGISTRATION_RESPONSE_TYPE,
      registrationService: process.env.CIP_REGISTRATION_SERVICE,
      isLocal: process.env.IS_LOCAL === 'true'
    },
    consent: {
      notificationOptions: process.env.NOTIFICATION_INTRO_OPTIONS,
      notificationsApp: process.env.SALESFORCE_IDP_APP,
      notificationsChannel
        : process.env.NOTIFICATION_DEFAULT_CHANNEL,
      notificationsVersion: process.env.NOTIFICATION_DEFAULT_VERSION
    },
    navigation: {
      bottomNavName: process.env.BOTTOM_NAV_ID,
      footerKey: process.env.FOOTER_KEY || 'footer',
      registrationFooterKey: process.env.REGISTRATION_FOOTER_KEY || 'registrationFooter',
      megaMenu: true,
      mobileNavBreakpoint: process.env.MOBILE_NAV_BREAKPOINT || 1280,
      navMainKey: process.env.NAV_KEY || 'main',
      navMobileKey:
        process.env.NAV_MOBILE_KEY ||
        (process.env.NAV_KEY && `${process.env.NAV_KEY}_mobile`) ||
        'main_mobile',
      searchUrl: '/catalog/search',
      topNavName: process.env.TOP_NAV_ID,
      // TODO move this to cms
      accounts: []
    },
    network: {
      sensitiveDisable: featureFlag(process.env.SKIP_SSR_QUERIES)
    },
    orders: {
      orderHistoryPageSize: process.env.ORDER_HISTORY_PAGE_SIZE ? parseInt(process.env.ORDER_HISTORY_PAGE_SIZE, 10) : 10
    },
    invoices: {
      invoicesPageSize: process.env.INVOICES_PAGE_SIZE ? parseInt(process.env.INVOICES_PAGE_SIZE, 10) : 10
    },
    cart: {
      maxCartCount: 100
    },
    checkout: {
      disableGuest: true,
      allowOrgAddress: false
    },
    image: {
      imageCdn: process.env.CDN_URL
    },
    oilFoxUrl: {
      oilFoxUrl: process.env.OILFOX_URL,
      telemetryExcludedCountries: process.env.TELEMETRY_EXCLUDED_COUNTRIES
    },
    events: {
      provider: process.env.EVENTS_PROVIDER
    },
    main: {
      jwtSecretKey: process.env.JWT_SIGNING,
      sessionStorageType: process.env.APP_STORE
    },
    contact: {
      emailSubject: process.env.EMAIL_SUBJECT,
      contactAttrId: process.env.CONTACT_US
    },
    excelUpload: {
      pollingInterval: 5000,
      pollingTimeout: 120000
    }
  },
  redirects: {
    '/': '/auth/landing',
    '/employee': '/auth/employee',
    '/my-company': '/my-company/my-organization',
    '/account-profile': '/account-profile/profile',
    '/cart': '/cart/cart',
    '/contact/contact-us': '/cart/contact-us',
    '/order': '/checkout/myorders',
    '/enquiries': '/checkout/myenquiries',
    '/asmLogin': '/auth/asmLogin',
    '/documents': '/checkout/documents'
    
  },
  client: new ApolloClient({
    link: linkWithAuthRetry,
    cache: cacheFactory().restore(
      // eslint-disable-next-line no-underscore-dangle
      typeof window === 'undefined' ? {} : window.__APOLLO_STATE__
    )
  })
};